<script>
    import {
        extractDatasetName,
        extractDatasetNameFromPath,
        extractPhaseRetrievalBaseFromPath,
        isoDateToDate,
        isoDateToTime,
        isNumeric,
    } from "../utils";
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';
    
    export let json;
    export let sliceIndex;
    export let previewType;
    export let phaseRetrievalDatasetIds;
    export let selectedPreview;
    let prunedJson;
    let tmpJson;

    $: {
        if (json) {
            tmpJson = structuredClone(json);
            prunedJson = tmpJson.map((p) => {
                p.ph_base = extractPhaseRetrievalBaseFromPath(p?.preview_output?.[0]?.large_image_pattern);
                p.ph_delta = p?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta;
                p.ph_mu = p?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu;
                p.cs_tolerance = p?.preview_input?.preview_input?.centershift_tolerance_object_mm;
                p.cs = p?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm;
                p.cs_isoptimized =
                    p?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_is_optimized;
                p.tilt = p?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_deg;
                p.tilt_isoptimized =
                    p?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_is_optimized;
                p.preview_type = p?.preview_input?.preview_input?.preview_type;
                p.calibration_id = p?.preview_input?.generic_preview_input?.calibration_id;
                delete p?.preview_input?.generic_preview_input?.calibration_id;
                delete p?.preview_input?.generic_preview_input?.phase_retrieval_settings;
                delete p?.preview_input?.generic_preview_input?.preprocessing_settings;
                delete p?.preview_input?.generic_preview_input?.reconstruction_settings;
                delete p?.preview_input?.generic_preview_input;
                delete p?.preview_input;
                p?.preview_output?.map((v) => {
                    delete v.max_in_float32;
                    delete v.min_in_float32;
                    if (p.preview_type == "CENTERSHIFT" || p.preview_type == "WIDE_CENTERSHIFT" ) {
                        let range = v.parameter_values?.length || 64;
                        let midrange = Math.floor((range - 1) / 2);
                        v.indexrange = range;
                        v.midrange = midrange;
                        v.cs = v.parameter_values?.[midrange];
                        v.cs_tolerance = Math.abs(v.parameter_values?.[range - 1] - v.parameter_values?.[0]) / 2;
                        v.range = range;
                    }
                    delete v.parameter_values;
                    delete v.thumbnail_image_pattern;
                    v.dataset = extractDatasetNameFromPath(v.large_image_pattern)?.[0];
                    delete v.large_image_pattern;
                    delete v.status;
                    return v;
                });
                return p;
            });
        }
    }
</script>

<div>
    <!--pre>{JSON.stringify(prunedJson, undefined, 2)}</pre-->
    <annotation style="font-family: monospace">
        previewType={previewType}
        sliceIndex={sliceIndex}
        phaseRetrievalDatasetIds={phaseRetrievalDatasetIds} ({phaseRetrievalDatasetIds?.length}) selectedPreview={extractDatasetName(
            selectedPreview,
            sliceIndex
        )}
        <ol reversed>
            {#if prunedJson}
                {#each prunedJson as p}
                    <li>
                        <span class={phaseRetrievalDatasetIds?.includes(p.ph_base) ? "highlight" : ""}>
                            {p.ph_base}
                        </span>
                        &nbsp; <span use:tooltip={{content: `δ=${p.ph_delta} (input)`, placement: "top"}}>δ={p.ph_delta?.toPrecision(3)}</span> μ={p.ph_mu}
                        &nbsp; {p.calibration_id}
                    </li>
                    <ul>
                        <span class={p.preview_type == previewType ? "highlight" : ""}>{p.preview_type}</span>
                        &nbsp; 
                        <span use:tooltip={{content: `cs=${p.cs} (input)`, placement: "top"}}>cs={(p.cs * 1000).toPrecision(3)}μm {isNumeric(p.cs_tolerance)
                            ? `±${(p.cs_tolerance * 1000).toPrecision(3)}`
                            : ""}
                        </span>
                        {p.cs_isoptimized ? "(optimised)" : ""} &nbsp; tilt={(p.tilt)?.toPrecision(3)}° {p.tilt_isoptimized
                            ? "(optimised)"
                            : ""}
                        <ul>
                            {#each p.preview_output as v}
                                <li>
                                    <span
                                        class={v.dataset == extractDatasetName(selectedPreview, sliceIndex)
                                            ? "highlight2"
                                            : ""}>
                                        {v.dataset}
                                    </span>
                                    :
                                    <span class={v.slice == sliceIndex ? "highlight" : ""}>
                                        {String(v.slice).padStart(4, "\u00a0")}
                                    </span>
                                    &nbsp;&nbsp; {isoDateToDate(v.start_time)}
                                    {isoDateToTime(v.start_time)} &nbsp; &nbsp;
                                    <span use:tooltip={{content: `cs=${v.cs} (output: middle index=${v.midrange}/[0..${v.indexrange-1}])`, placement: "top"}}>
                                        {v.cs
                                            ? `cs=${(v.cs * 1000).toPrecision(3)}μm`
                                            : ""}
                                    </span>
                                    {v.cs_tolerance ? `±${(v.cs_tolerance * 1000).toPrecision(3)}` : ""}
                                    &nbsp;[{((v.cs - v.cs_tolerance) * 1000).toPrecision(3)}..{((v.cs + v.cs_tolerance) * 1000).toPrecision(3)}]
                                    &nbsp;&nbsp;
                                    max_vol={v.maximum_volume?.max_slice}x{v.maximum_volume?.slice_roi.right}x{v.maximum_volume?.slice_roi.bot}
                                </li>
                            {/each}
                        </ul>
                    </ul>
                {/each}
            {/if}
        </ol>
    </annotation>
</div>

<style>
    .highlight,
    .highlight2 {
        font-weight: bold;
    }

    .highlight2 {
        color: brown;
    }
</style>
